import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "./services/supabase";
import { sessionService } from "./services/sessionService";
import { wishlistService } from "./services/wishlistService";
import { profileService } from "./services/profileService";
import storageService from "./services/storageService";
import API_URL from './config/api';

// Loading Components
import { FullScreenLoader, PageTransitionLoader, LoaderStyles } from './components/LoadingScreen';

// Auth Screens
import Login from "./authscreens/Login";
import Register from "./authscreens/Register";
import ForgotPassword from "./authscreens/ForgotPassword";
import CheckEmail from "./authscreens/CheckEmail";
import ResetPassword from "./authscreens/ResetPassword";
import PasswordChanged from "./authscreens/PasswordChange";
import RegistrationCheckEmail from "./authscreens/RegistrationCheckEmail";
import ErrorScreen from "./authscreens/ErrorScreen";

// App Screens
import MaintenanceMode from "./screens/MaintenanceMode";
import GetStartedPage from "./screens/GetStartedPage";
import HomeScreen from "./screens/HomeScreen";
import ProductDetailPage from "./screens/ProductDetailPage";
import AIFinderScreen from "./screens/AIFinderScreen";
import WishlistScreen from "./screens/WishlistScreen";
import ProfileScreen from "./screens/ProfileScreen";

// Valid screens that can be restored from URL
const VALID_SCREENS = ['home', 'aiFinder', 'wishlist', 'profile', 'productDetail'];
const AUTH_REQUIRED_SCREENS = ['wishlist', 'profile', 'productDetail'];

// Helper to get screen from URL hash
const getScreenFromHash = () => {
  const hash = window.location.hash.replace('#', '');
  
  // Handle product detail URLs like #product-B08XYZ123
  if (hash.startsWith('product-')) {
    return { screen: 'productDetail', productId: hash.replace('product-', '') };
  }
  
  // Handle regular screen hashes
  if (VALID_SCREENS.includes(hash)) {
    return { screen: hash, productId: null };
  }
  
  return { screen: null, productId: null };
};

// Navigation Stack Manager - Enhanced with Browser History API
class NavigationManager {
  constructor() {
    this.stack = ['home'];
    this.maxStackSize = 20;
    this.listeners = new Set();
    this.isPopState = false;
  }

  getCurrentScreen() {
    return this.stack[this.stack.length - 1] || 'home';
  }

  getPreviousScreen() {
    return this.stack.length > 1 ? this.stack[this.stack.length - 2] : null;
  }

  push(screen, updateHistory = true) {
    if (this.getCurrentScreen() === screen) return;
    
    const mainTabs = ['home', 'aiFinder', 'wishlist', 'profile'];
    if (mainTabs.includes(screen)) {
      if (screen === 'home') {
        this.stack = ['home'];
      } else {
        this.stack = ['home', screen];
      }
    } else {
      this.stack.push(screen);
      if (this.stack.length > this.maxStackSize) {
        this.stack = this.stack.slice(-this.maxStackSize);
        if (this.stack[0] !== 'home') {
          this.stack.unshift('home');
        }
      }
    }
    
    if (updateHistory && !this.isPopState) {
      const state = { screen, stack: [...this.stack] };
      window.history.pushState(state, '', `#${screen}`);
    }
    this.isPopState = false;
    
    this.notifyListeners();
  }

  pop(updateHistory = true) {
    if (this.stack.length > 1) {
      this.stack.pop();
      
      if (updateHistory && !this.isPopState) {
        window.history.back();
      }
      this.isPopState = false;
      
      this.notifyListeners();
      return this.getCurrentScreen();
    }
    return 'home';
  }

  handlePopState(state) {
    this.isPopState = true;
    if (state && state.stack) {
      this.stack = state.stack;
      this.notifyListeners();
      return state.screen;
    } else {
      this.stack = ['home'];
      this.notifyListeners();
      return 'home';
    }
  }

  canGoBack() {
    return this.stack.length > 1;
  }

  reset(updateHistory = true) {
    this.stack = ['home'];
    if (updateHistory && !this.isPopState) {
      window.history.replaceState({ screen: 'home', stack: ['home'] }, '', '#home');
    }
    this.isPopState = false;
    this.notifyListeners();
  }

  // Set stack without triggering history update (for restoring from URL)
  setStack(stack) {
    this.stack = stack;
    this.notifyListeners();
  }

  getStack() {
    return [...this.stack];
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.getStack()));
  }

  initializeHistory(screen = 'home') {
    const state = { screen, stack: [...this.stack] };
    window.history.replaceState(state, '', `#${screen}`);
  }
}

const navigationManager = new NavigationManager();
const SITE_UNDER_MAINTENANCE = false;

// Helper to check if we're in OAuth callback
const isOAuthCallback = () => {
  const params = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  
  if (params.has('code')) return true;
  if (hash.includes('access_token') || hash.includes('#access_token')) return true;
  
  return false;
};

export default function App() {
  // Core State
  const [screen, setScreen] = useState(null);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasSeenGetStarted, setHasSeenGetStarted] = useState(false);

  // Loading States
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingWishlist, setIsLoadingWishlist] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Auth State
  const [resetEmail, setResetEmail] = useState("");
  const [registrationEmail, setRegistrationEmail] = useState("");
  const [errorType, setErrorType] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  
  // Product State
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productHistory, setProductHistory] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  
  // Navigation & Guest State
  const [navigationData, setNavigationData] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // Pending screen restoration (for product detail after products load)
  const [pendingProductId, setPendingProductId] = useState(null);
  
  // Refs
  const isLoggingOutRef = useRef(false);
  const isInitializingRef = useRef(false);
  const userRef = useRef(null);
  const isManualLoginRef = useRef(false);
  const pendingActionRef = useRef(null);
  const hasHandledCallback = useRef(false);
  const isHandlingOAuth = useRef(false);
  const selectedProductRef = useRef(null);
  const productHistoryRef = useRef([]);

  // Keep refs in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    pendingActionRef.current = pendingAction;
  }, [pendingAction]);

  useEffect(() => {
    selectedProductRef.current = selectedProduct;
  }, [selectedProduct]);

  useEffect(() => {
    productHistoryRef.current = productHistory;
  }, [productHistory]);

  const clearAllState = useCallback(() => {
  setUser(null);
  userRef.current = null;
  setUserProfile(null);
  setWishlist([]);
  setSelectedProduct(null);
  selectedProductRef.current = null;
  setProductHistory([]);
  productHistoryRef.current = [];
  setNavigationData(null);
  setPendingAction(null);
  pendingActionRef.current = null;
  setShowLoginModal(false);
  setPendingProductId(null);
  navigationManager.reset();
  // REMOVED: setScreen("home");  // Navigation should be handled by callers
  storageService.clearNavigationState();
}, []);

  // Handle pending product restoration after products load
  useEffect(() => {
    if (pendingProductId && allProducts.length > 0 && user) {
      const product = allProducts.find(p => p.asin === pendingProductId);
      if (product) {
        setSelectedProduct(product);
        selectedProductRef.current = product;
        setScreen('productDetail');
        navigationManager.setStack(['home', 'productDetail']);
        navigationManager.initializeHistory('productDetail');
      } else {
        // Product not found, go to home
        setScreen('home');
        navigationManager.reset();
      }
      setPendingProductId(null);
    }
  }, [pendingProductId, allProducts, user]);

  // Browser History PopState Handler
  useEffect(() => {
    const handlePopState = (event) => {
      console.log('PopState event:', event.state);
      
      if (screen === 'productDetail') {
        if (productHistoryRef.current.length > 0) {
          const newHistory = [...productHistoryRef.current];
          const previousProduct = newHistory.pop();
          setProductHistory(newHistory);
          setSelectedProduct(previousProduct);
          
          window.history.pushState(
            { screen: 'productDetail', stack: navigationManager.getStack() }, 
            '', 
            '#productDetail'
          );
          return;
        }
      }
      
      const newScreen = navigationManager.handlePopState(event.state);
      
      if (newScreen !== 'productDetail') {
        setSelectedProduct(null);
        selectedProductRef.current = null;
        setProductHistory([]);
        productHistoryRef.current = [];
      }
      
      setScreen(newScreen);
      
      setTimeout(() => {
        if (newScreen === 'home') {
          const savedPos = sessionStorage.getItem('homeScrollPos');
          if (savedPos) window.scrollTo({ top: parseInt(savedPos), behavior: 'instant' });
        } else if (newScreen === 'aiFinder') {
          const savedPos = sessionStorage.getItem('aiFinderScrollPos');
          if (savedPos) window.scrollTo({ top: parseInt(savedPos), behavior: 'instant' });
        }
      }, 50);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [screen]);

  // Timeout failsafe
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isInitialized) {
        console.error("Initialization timeout - forcing ready state");
        setIsInitialized(true);
        if (!screen) setScreen(hasSeenGetStarted ? "home" : "getStarted");
      }
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [isInitialized, screen, hasSeenGetStarted]);

  // Check get started flag
  useEffect(() => {
    const getStartedSeen = localStorage.getItem('giveaura_get_started_seen');
    if (getStartedSeen === 'true') {
      setHasSeenGetStarted(true);
    }
  }, []);

  // Products loading
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const cached = storageService.getCachedProducts();
        if (cached && cached.length > 0) {
          setAllProducts(cached);
          setIsLoadingProducts(false);
          fetchFreshProducts();
          return;
        }
        await fetchFreshProducts();
      } catch (e) {
        console.error("Failed to fetch products", e);
        setAllProducts([]);
        setIsLoadingProducts(false);
      }
    };

    const fetchFreshProducts = async () => {
      try {
        const res = await fetch(`${API_URL}/products/all`);
        const data = await res.json();
        const products = Array.isArray(data) ? data : [];
        setAllProducts(products);
        storageService.cacheProducts(products);
      } catch (err) {
        console.error("Product fetch error:", err);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  // User data loading
  const loadUserData = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const profile = await profileService.getProfile(userId);
      setUserProfile(profile);
      
      setIsLoadingWishlist(true);
      const cachedWishlist = storageService.getCachedWishlist();
      if (cachedWishlist) setWishlist(cachedWishlist);

      const wishlistData = await wishlistService.getWishlist(userId);
      setWishlist(wishlistData);
      storageService.cacheWishlist(wishlistData);
    } catch (error) {
      console.error("Error loading user data:", error);
    } finally {
      setIsLoadingWishlist(false);
    }
  }, []);

  // Handle OAuth Callback
  useEffect(() => {
    const handleAuthCallback = async () => {
      if (!isOAuthCallback() || hasHandledCallback.current || isHandlingOAuth.current) {
        return;
      }
      
      isHandlingOAuth.current = true;
      hasHandledCallback.current = true;
      setIsAuthLoading(true);
      
      console.log("=== OAuth Callback Detected ===");
      
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          throw sessionError;
        }

        if (session?.user) {
          console.log("✓ OAuth successful:", session.user.email);
          
          userRef.current = session.user;
          setUser(session.user);
          
          await sessionService.createSession(session.user);
          await loadUserData(session.user.id);
          
          localStorage.setItem('giveaura_get_started_seen', 'true');
          setHasSeenGetStarted(true);
          
          window.history.replaceState(null, "", "/");
          navigationManager.reset();
          setScreen("home");
          setIsInitialized(true);
          setIsAuthLoading(false);
        } else {
          console.log("No session yet, retrying...");
          
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          const { data: { session: retrySession }, error: retryError } = await supabase.auth.getSession();
          
          if (retryError) {
            throw retryError;
          }
          
          if (retrySession?.user) {
            console.log("✓ OAuth successful on retry:", retrySession.user.email);
            
            userRef.current = retrySession.user;
            setUser(retrySession.user);
            await sessionService.createSession(retrySession.user);
            await loadUserData(retrySession.user.id);
            
            localStorage.setItem('giveaura_get_started_seen', 'true');
            setHasSeenGetStarted(true);
            
            window.history.replaceState(null, "", "/");
            navigationManager.reset();
            setScreen("home");
          } else {
            console.error("OAuth failed - no session found");
            setErrorType("Authentication failed. Please try again.");
            setScreen("error");
          }
          
          setIsInitialized(true);
          setIsAuthLoading(false);
        }
      } catch (err) {
        console.error("OAuth callback error:", err);
        setErrorType(err.message || "Authentication failed");
        setScreen("error");
        setIsInitialized(true);
        setIsAuthLoading(false);
      } finally {
        isHandlingOAuth.current = false;
      }
    };

    handleAuthCallback();
  }, [hasSeenGetStarted, loadUserData]);

  // Normal App Initialization - Enhanced with URL hash restoration
  useEffect(() => {
    if (isInitializingRef.current) return;
    if (isOAuthCallback()) return;
    if (hasHandledCallback.current) return;
    if (userRef.current) return;
    
    isInitializingRef.current = true;
    
    const initializeApp = async () => {
      console.log("=== APP INITIALIZATION STARTED ===");
      
      // Check localStorage first
      const getStartedSeen = localStorage.getItem('giveaura_get_started_seen') === 'true';
      
      // Get screen from URL hash
      const { screen: hashScreen, productId } = getScreenFromHash();
      console.log("URL hash screen:", hashScreen, "Product ID:", productId);
      
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error("Session error:", sessionError);
        }

        if (session?.user) {
          console.log("Existing session:", session.user.email);
          userRef.current = session.user;
          setUser(session.user);
          await sessionService.createSession(session.user);
          await loadUserData(session.user.id);
          localStorage.setItem('giveaura_get_started_seen', 'true');
          setHasSeenGetStarted(true);
          
          // Restore screen from URL hash if valid
          if (hashScreen && VALID_SCREENS.includes(hashScreen)) {
            if (hashScreen === 'productDetail' && productId) {
              // Set pending product ID - will be handled when products load
              setPendingProductId(productId);
              // Set temporary screen while waiting for products
              setScreen('home');
              navigationManager.setStack(['home']);
            } else {
              // Restore the screen
              if (hashScreen === 'home') {
                navigationManager.setStack(['home']);
              } else {
                navigationManager.setStack(['home', hashScreen]);
              }
              setScreen(hashScreen);
              navigationManager.initializeHistory(hashScreen);
            }
          } else {
            // No valid hash, go to home
            navigationManager.reset();
            setScreen("home");
          }
        } else {
          console.log("Guest mode");
          
          // For guests, only restore non-auth-required screens
          if (getStartedSeen) {
            if (hashScreen && !AUTH_REQUIRED_SCREENS.includes(hashScreen)) {
              setScreen(hashScreen);
              if (hashScreen === 'home') {
                navigationManager.setStack(['home']);
              } else {
                navigationManager.setStack(['home', hashScreen]);
              }
              navigationManager.initializeHistory(hashScreen);
            } else {
              setScreen("home");
              navigationManager.reset();
            }
          } else {
            setScreen("getStarted");
          }
        }

        setIsInitialized(true);
        
      } catch (error) {
        console.error("Initialization error:", error);
        setScreen(getStartedSeen ? "home" : "getStarted");
        setIsInitialized(true);
      }
      
      console.log("=== APP INITIALIZATION COMPLETED ===");
    };

    initializeApp();
  }, [hasSeenGetStarted, loadUserData]);

  // Auth State Listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth event:", event, "Manual login:", isManualLoginRef.current);
      
      if (isHandlingOAuth.current) {
        console.log("Ignoring auth event - OAuth in progress");
        return;
      }
      
      if (event === "SIGNED_IN" && session?.user) {
        if (isManualLoginRef.current) {
          console.log("Ignoring SIGNED_IN event - manual login in progress");
          isManualLoginRef.current = false;
          return;
        }
        
        if (userRef.current?.id !== session.user.id) {
          console.log("Auth state changed - new user:", session.user.email);
          userRef.current = session.user;
          setUser(session.user);
          
          if (!sessionService.currentSessionId) {
            await sessionService.createSession(session.user);
          }
          
          await loadUserData(session.user.id);
          localStorage.setItem('giveaura_get_started_seen', 'true');
          setHasSeenGetStarted(true);
          
          if (!screen || screen === 'getStarted' || screen === 'login') {
            navigationManager.reset();
            setScreen("home");
          }
        }
      }

      if (event === "SIGNED_OUT") {
  clearAllState();
  // Only go to home if not in the middle of an intentional logout
  // (logout handler will set its own destination)
  if (!isLoggingOutRef.current) {
    setScreen("getStarted");
  }
}

      if (event === "TOKEN_REFRESHED") {
        sessionService.updateSessionActivity();
      }

      if (event === "PASSWORD_RECOVERY") {
        setScreen("reset");
      }
    });

    return () => subscription.unsubscribe();
  }, [clearAllState, loadUserData, screen]);

  const handleLogout = useCallback(async () => {
  if (isLoggingOutRef.current) return;
  isLoggingOutRef.current = true;
  
  try {
    await sessionService.endSession();
    clearAllState();
    await supabase.auth.signOut();
    
    // After logout, force GetStarted page
    setScreen("getStarted");
    setHasSeenGetStarted(false);
    localStorage.removeItem('giveaura_get_started_seen');
    
  } catch (error) {
    console.error("Logout error:", error);
    clearAllState();
    setScreen("getStarted");
    setHasSeenGetStarted(false);
  } finally {
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  }
}, [clearAllState]);

  const handleNavigate = useCallback((targetScreen, data = null) => {
    if (!userRef.current && (targetScreen === 'wishlist' || targetScreen === 'profile')) {
      setPendingAction({ type: targetScreen === 'wishlist' ? 'viewWishlist' : 'viewProfile' });
      pendingActionRef.current = { type: targetScreen === 'wishlist' ? 'viewWishlist' : 'viewProfile' };
      setShowLoginModal(true);
      return;
    }
    
    if (targetScreen !== 'productDetail') {
      setSelectedProduct(null);
      selectedProductRef.current = null;
      setProductHistory([]);
      productHistoryRef.current = [];
    }
    
    setIsTransitioning(true);
    setNavigationData(data);
    navigationManager.push(targetScreen);
    
    setTimeout(() => {
      setScreen(targetScreen);
      setIsTransitioning(false);
      if (targetScreen !== 'productDetail') {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    }, 100);
  }, []);

  const handleProductSelect = useCallback((product, fromScreen = null, loggedInUser = null) => {
    const currentUser = loggedInUser || userRef.current;
    
    if (!currentUser) {
      const action = { type: 'viewProduct', product, fromScreen: fromScreen || screen };
      setPendingAction(action);
      pendingActionRef.current = action;
      setShowLoginModal(true);
      return;
    }
    
    // Save scroll position
    if (screen === 'home') {
      sessionStorage.setItem('homeScrollPos', window.scrollY.toString());
    } else if (screen === 'aiFinder') {
      sessionStorage.setItem('aiFinderScrollPos', window.scrollY.toString());
    }
    
    // Handle product history for back navigation within product details
    if (screen === 'productDetail' && selectedProductRef.current) {
      const newHistory = [...productHistoryRef.current, selectedProductRef.current];
      setProductHistory(newHistory);
      productHistoryRef.current = newHistory;
    } else {
      setProductHistory([]);
      productHistoryRef.current = [];
      navigationManager.push(fromScreen || screen);
    }
    
    setIsTransitioning(true);
    
    setTimeout(() => {
      setSelectedProduct(product);
      selectedProductRef.current = product;
      setScreen("productDetail");
      
      // Push product detail to history
      const state = { screen: 'productDetail', stack: navigationManager.getStack(), productId: product.asin };
      window.history.pushState(state, '', `#product-${product.asin}`);
      
      setIsTransitioning(false);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 100);
  }, [screen]);

  const handleLoginSuccess = useCallback(async (loginUser) => {
    console.log("Login success handler called:", loginUser.email);
    
    isManualLoginRef.current = true;
    userRef.current = loginUser;
    setUser(loginUser);
    
    try {
      await sessionService.createSession(loginUser);
      await loadUserData(loginUser.id);
      
      localStorage.setItem('giveaura_get_started_seen', 'true');
      setHasSeenGetStarted(true);
      
      const action = pendingActionRef.current;
      
      if (action) {
        console.log("Handling pending action:", action);
        
        if (action.type === 'viewProduct') {
          handleProductSelect(action.product, action.fromScreen, loginUser);
        } else if (action.type === 'addWishlist') {
          setWishlist(prev => [...prev, action.asin]);
          await wishlistService.addToWishlist(loginUser.id, action.asin);
          setScreen('home');
        } else if (action.type === 'viewWishlist') {
          setScreen('wishlist');
        } else if (action.type === 'viewProfile') {
          setScreen('profile');
        }
        setPendingAction(null);
        pendingActionRef.current = null;
      } else {
        console.log("No pending action, going home");
        navigationManager.reset();
        setScreen("home");
      }
    } catch (error) {
      console.error("Error in login success handler:", error);
      navigationManager.reset();
      setScreen("home");
    }
  }, [handleProductSelect, loadUserData]);

  const handleBackFromProduct = useCallback((newProduct = null) => {
    // If a new product is passed, navigate to that product instead of going back
    if (newProduct && newProduct.asin) {
      handleProductSelect(newProduct);
      return;
    }
    
    if (productHistoryRef.current.length > 0) {
      const newHistory = [...productHistoryRef.current];
      const previousProduct = newHistory.pop();
      setProductHistory(newHistory);
      productHistoryRef.current = newHistory;
      setSelectedProduct(previousProduct);
      selectedProductRef.current = previousProduct;
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      setSelectedProduct(null);
      selectedProductRef.current = null;
      setProductHistory([]);
      productHistoryRef.current = [];
      
      // Use navigation manager to go back properly
      navigationManager.isPopState = true; // Prevent double history update
      const prevScreen = navigationManager.pop(false);
      setScreen(prevScreen);
      
      // Go back in browser history
      window.history.back();
      
      setTimeout(() => {
        if (prevScreen === 'home') {
          const savedPos = sessionStorage.getItem('homeScrollPos');
          if (savedPos) window.scrollTo({ top: parseInt(savedPos), behavior: 'instant' });
        } else if (prevScreen === 'aiFinder') {
          const savedPos = sessionStorage.getItem('aiFinderScrollPos');
          if (savedPos) window.scrollTo({ top: parseInt(savedPos), behavior: 'instant' });
        }
      }, 50);
    }
  }, [handleProductSelect]);

  const toggleWishlist = useCallback(async (asin) => {
    if (!userRef.current) {
      const action = { type: 'addWishlist', asin };
      setPendingAction(action);
      pendingActionRef.current = action;
      setShowLoginModal(true);
      return;
    }

    const currentUser = userRef.current;
    const isInWishlist = wishlist.includes(asin);
    setWishlist(prev => isInWishlist ? prev.filter(id => id !== asin) : [...prev, asin]);
    
    const newWishlist = isInWishlist ? wishlist.filter(id => id !== asin) : [...wishlist, asin];
    storageService.cacheWishlist(newWishlist);

    try {
      const success = isInWishlist
        ? await wishlistService.removeFromWishlist(currentUser.id, asin)
        : await wishlistService.addToWishlist(currentUser.id, asin);

      if (!success) {
        setWishlist(prev => isInWishlist ? [...prev, asin] : prev.filter(id => id !== asin));
      }
    } catch (error) {
      console.error("Wishlist toggle error:", error);
      setWishlist(prev => isInWishlist ? [...prev, asin] : prev.filter(id => id !== asin));
    }
  }, [wishlist]);

  const handleProfileUpdate = async (updates) => {
    if (!userRef.current) return false;
    const result = await profileService.updateProfile(userRef.current.id, updates);
    if (result) setUserProfile(result);
    return !!result;
  };

  const handleGetStarted = useCallback(() => {
    localStorage.setItem('giveaura_get_started_seen', 'true');
    setHasSeenGetStarted(true);
    setScreen("home");
    navigationManager.reset();
  }, []);

  if (!isInitialized || screen === null) {
    return (
      <>
        <LoaderStyles />
        <FullScreenLoader message="Loading GiveAura..." />
      </>
    );
  }

  if (isAuthLoading) {
    return (
      <>
        <LoaderStyles />
        <FullScreenLoader message="Completing sign in..." />
      </>
    );
  }

  const commonProps = {
    user, 
    userProfile, 
    onLogout: handleLogout, 
    currentScreen: screen,
    onNavigate: handleNavigate, 
    canGoBack: navigationManager.canGoBack(),
    showLoginModal, 
    setShowLoginModal,
  };

  if (SITE_UNDER_MAINTENANCE) {
    return (
      <>
        <LoaderStyles />
        <MaintenanceMode isEnabled={true} />
      </>
    );
  }

  return (
    <>
      <LoaderStyles />
      {isTransitioning && <PageTransitionLoader />}
      
      <div style={{ display: screen === 'home' ? 'block' : 'none' }}>
        <HomeScreen
          {...commonProps}
          onProductSelect={(product) => handleProductSelect(product, 'home')}
          products={allProducts}
          productsLoading={isLoadingProducts}
          wishlist={wishlist}
          toggleWishlist={toggleWishlist}
          wishlistLoading={isLoadingWishlist}
        />
      </div>

      <div style={{ display: screen === 'aiFinder' ? 'block' : 'none' }}>
        <AIFinderScreen
          {...commonProps}
          products={allProducts}
          onProductSelect={(product) => handleProductSelect(product, 'aiFinder')}
          wishlist={wishlist}
          toggleWishlist={toggleWishlist}
          navigationData={navigationData}
        />
      </div>

      {user && (
        <div style={{ display: screen === 'wishlist' ? 'block' : 'none' }}>
          <WishlistScreen
            {...commonProps}
            products={allProducts}
            wishlist={wishlist}
            toggleWishlist={toggleWishlist}
            onProductSelect={(product) => handleProductSelect(product, 'wishlist')}
            wishlistLoading={isLoadingWishlist}
          />
        </div>
      )}

      {user && (
        <div style={{ display: screen === 'profile' ? 'block' : 'none' }}>
          <ProfileScreen
            {...commonProps}
            wishlistCount={wishlist.length}
            onProfileUpdate={handleProfileUpdate}
          />
        </div>
      )}

      {screen === "productDetail" && selectedProduct && user && (
        <div style={{ position: 'relative', zIndex: 50 }}>
          <ProductDetailPage
            {...commonProps}
            product={selectedProduct}
            onClose={handleBackFromProduct}
            onProductSelect={(product) => handleProductSelect(product)}
            allProducts={allProducts}
            wishlist={wishlist}
            toggleWishlist={toggleWishlist}
            previousScreen={navigationManager.getPreviousScreen()}
            canGoBackInProducts={productHistory.length > 0}
          />
        </div>
      )}

      {screen === "getStarted" && (
        <GetStartedPage
          onGetStarted={handleGetStarted}
          onLogin={() => setScreen("login")}
          onRegister={() => setScreen("register")}
        />
      )}

      {screen === "error" && (
        <ErrorScreen errorType={errorType} onBackToLogin={() => setScreen("login")} />
      )}
      
      {screen === "login" && (
        <Login
          onForgotPassword={() => setScreen("forgot")}
          onRegister={() => setScreen("register")}
          onBack={() => {
            setPendingAction(null);
            pendingActionRef.current = null;
            setShowLoginModal(false);
            setScreen(hasSeenGetStarted ? "home" : "getStarted");
          }}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {screen === "register" && (
        <Register
          onBack={() => setScreen("login")}
          onRegisterSuccess={email => {
            setRegistrationEmail(email);
            setScreen("registrationCheckEmail");
          }}
        />
      )}

      {screen === "registrationCheckEmail" && (
        <RegistrationCheckEmail email={registrationEmail} onBackToLogin={() => setScreen("login")} />
      )}

      {screen === "forgot" && (
        <ForgotPassword
          onBack={() => setScreen("login")}
          onEmailSent={email => {
            setResetEmail(email);
            setScreen("checkEmail");
          }}
        />
      )}

      {screen === "checkEmail" && <CheckEmail email={resetEmail} onResend={() => setScreen("forgot")} />}
      {screen === "reset" && <ResetPassword onSuccess={() => setScreen("changed")} />}
      {screen === "changed" && <PasswordChanged onLoginNow={() => setScreen("login")} />}
    </>
  );
}